import React from 'react';
import { useState, useEffect, useRef, useCallback, useSyncExternalStore } from 'react';
import { createRoot } from 'react-dom/client';
import './classes/QuickAppPopup.css';
import {
    COURSES, DEGREES, GRADES, HEARD, GENDERS, QUICK_APP_MESSAGES, QUICK_APP_DONE_URL,
    subscribeQuickApp, getQuickApp, openQuickApp, closeQuickApp,
    checkQuickAppStep2, saveQuickAppStep2, checkQuickAppStep3, saveQuickAppStep3,
} from './services/QuickAppService';

// Steps 2 and 3 of the application a quick form started (see QuickAppService), in a popup,
// as the Drupal theme's drop-in shows them.

const TITLES = { 2: 'Step 2 of 3: About you', 3: 'Step 3 of 3: Your goals', 4: 'Application received' };
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'));
const THIS_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: THIS_YEAR - 1899 }, (_, i) => String(THIS_YEAR - i));

// What the two steps ask for
const STEP_FIELDS = ['street_address', 'field_street_2', 'postal_code', 'field_secondary_phone', 'birthdayMonth',
    'birthdayDay', 'birthdayYear', 'gender', 'appliedCourses', 'cuc_degrees', 'story', 'field_current_job',
    'field_highest_grade', 'field_university', 'field_major', 'field_last_attended', 'field_military',
    'field_military_id', 'field_sports_recom', 'field_linkedin_url'];

const Req = () => <>{' '}<span className="req" aria-hidden="true">*</span></>;

// Focus a field, or the first field inside a form
function focusField(el) {
    if (el && el.tagName === 'FORM') el = el.querySelector('input, select, textarea');
    if (el) el.focus({ preventScroll: true });
}

const QuickAppDialog = ({ app }) => {
    const { state, stage, open } = app;
    const [fields, setFields] = useState(() => {
        const start = {};
        STEP_FIELDS.forEach((name) => { start[name] = state[name]; });
        return start;
    });
    const [errors, setErrors] = useState({});
    // The submit button's label while its step is being checked or saved
    const [busy, setBusy] = useState('');
    const overlayRef = useRef(null);
    const dialogRef = useRef(null);
    // Typing in the open step that is not saved yet
    const dirty = useRef(false);
    const shownStage = useRef(0);
    const scrollTop = useRef(0);
    const lastField = useRef(null);
    const lastFocus = useRef(null);

    const close = useCallback(() => {
        if (!getQuickApp()?.open) return;
        scrollTop.current = dialogRef.current.scrollTop;
        closeQuickApp();
    }, []);

    // While it is open the page behind does not scroll; closing gives the focus back
    useEffect(() => {
        if (!open) return;
        lastFocus.current = document.activeElement;
        document.documentElement.classList.add('sqa-lock');
        return () => {
            document.documentElement.classList.remove('sqa-lock');
            if (lastFocus.current?.focus) lastFocus.current.focus();
        };
    }, [open]);

    // A new step starts at its top with the cursor in its first field. Reopening at the same
    // step keeps the popup as it was left, typing included, and puts the cursor back where it was.
    useEffect(() => {
        if (!open) return;
        const dialog = dialogRef.current;
        const firstField = dialog.querySelector('.sqa-bd form, .sqa-done a');
        if (shownStage.current !== stage) {
            shownStage.current = stage;
            dialog.scrollTop = 0;
            focusField(firstField);
        } else {
            dialog.scrollTop = scrollTop.current;
            focusField(dialog.contains(lastField.current) ? lastField.current : firstField);
        }
    }, [open, stage]);

    // Bring the first problem into view
    useEffect(() => {
        const first = dialogRef.current.querySelector('[aria-invalid="true"]');
        if (!first) return;
        if (first.scrollIntoView) first.scrollIntoView({ block: 'center', behavior: 'smooth' });
        focusField(first);
    }, [errors]);

    useEffect(() => {
        // Saved steps are on the server; only typing in the open step is lost on leaving, so ask first
        const askBeforeLeaving = (e) => {
            if (!dirty.current) return;
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', askBeforeLeaving);
        // Going back to another page of the site puts the popup away
        window.addEventListener('popstate', close);
        return () => {
            window.removeEventListener('beforeunload', askBeforeLeaving);
            window.removeEventListener('popstate', close);
        };
    }, [close]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        dirty.current = true;
        setFields((prev) => ({
            ...prev,
            // A course listed twice (FIFA Agent Prep) is one value, so both its boxes follow it
            [name]: type === 'checkbox' ? prev[name].filter((v) => v !== value).concat(checked ? value : []) : value,
        }));
    };

    const trapKeys = (e) => {
        if (e.key === 'Escape') { close(); return; }
        if (e.key !== 'Tab') return;
        const focusable = [...overlayRef.current.querySelectorAll('button, [href], input, select, textarea')]
            .filter((el) => !el.disabled && el.offsetParent !== null);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };

    // The field in use, kept as focus moves (clicking X moves focus to the X button)
    const rememberField = (e) => {
        if (/^(INPUT|SELECT|TEXTAREA)$/.test(e.target.tagName)) lastField.current = e.target;
    };

    const submitStep2 = async (e) => {
        e.preventDefault();
        if (busy) return;
        setErrors({});
        setBusy('Checking…');
        const problems = await checkQuickAppStep2(fields);
        if (Object.keys(problems).length > 0) {
            setBusy('');
            setErrors(problems);
            return;
        }
        setBusy('Saving…');
        const saved = await saveQuickAppStep2(fields);
        setBusy('');
        if (saved) dirty.current = false;
        else setErrors({ save: QUICK_APP_MESSAGES.save });
    };

    const submitStep3 = async (e) => {
        e.preventDefault();
        if (busy) return;
        const problems = checkQuickAppStep3(fields);
        setErrors(problems);
        if (Object.keys(problems).length > 0) return;
        setBusy('Saving…');
        const saved = await saveQuickAppStep3(fields);
        setBusy('');
        if (saved) dirty.current = false;
        else setErrors({ save: QUICK_APP_MESSAGES.save });
    };

    // Marks a control (or a group of them) as wrong and points it at its message
    const invalid = (name) => (errors[name] ? { 'aria-invalid': 'true', 'aria-describedby': 'sqa-err-' + name } : {});
    const message = (name) => errors[name] && (
        <span className="sqa-err" id={'sqa-err-' + name} role="alert">{errors[name]}</span>
    );
    const control = (name) => ({ className: 'sqa-in', name, value: fields[name], onChange: handleChange, ...invalid(name) });
    const options = (list, placeholder) => (
        <>
            <option value="">{placeholder}</option>
            {list.map((item) => <option key={item} value={item}>{item}</option>)}
        </>
    );
    const radios = (name, list) => list.map((item) => (
        <label key={item}>
            <input type="radio" name={name} value={item} checked={fields[name] === item} onChange={handleChange} />
            {item}
        </label>
    ));
    const checkboxes = (name, list) => list.map(([code, label]) => (
        <label key={code}>
            <input type="checkbox" name={name} value={code} checked={fields[name].includes(code)} onChange={handleChange} />
            <span>{label}</span>
        </label>
    ));
    const submitButton = (label) => (
        <>
            <button type="submit" className="sqa-btn" aria-disabled={busy ? 'true' : undefined}>{busy || label}</button>
            {message('save')}
        </>
    );

    return (
        <div className="sqa-ov" ref={overlayRef} hidden={!open} onKeyDown={trapKeys} onFocus={rememberField}>
            <div className="sqa-dlg" ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="sqa-title">
                <div className="sqa-hd">
                    <h2 id="sqa-title">{TITLES[stage]}</h2>
                    <div className="sqa-steps" aria-hidden="true">
                        {[1, 2, 3].map((step) => <i key={step} className={step <= stage ? 'on' : ''} />)}
                    </div>
                    <button type="button" className="sqa-x" aria-label="Close (you can finish later)" onClick={close}>×</button>
                </div>
                <div className="sqa-bd">
                    {stage === 2 && (
                        <>
                            {state.showWelcomeTextStep2 && (
                                <p className="sqa-lead">Thank you for getting started. To see if you qualify for acceptance, please fill out the rest of the form.</p>
                            )}
                            <form noValidate onSubmit={submitStep2}>
                                <div className="sqa-row">
                                    <div className="sqa-f">
                                        <label htmlFor="sqa-street">Street Address<Req /></label>
                                        <input id="sqa-street" autoComplete="address-line1" {...control('street_address')} />
                                        {message('street_address')}
                                    </div>
                                    <div className="sqa-f">
                                        <label htmlFor="sqa-street2">Address (cont.)</label>
                                        <input id="sqa-street2" autoComplete="address-line2" {...control('field_street_2')} />
                                    </div>
                                </div>
                                <div className="sqa-row">
                                    <div className="sqa-f">
                                        <label htmlFor="sqa-zip">Zip/Postal Code<Req /></label>
                                        <input id="sqa-zip" autoComplete="postal-code" inputMode={state.country === 'US' ? 'numeric' : undefined} {...control('postal_code')} />
                                        {message('postal_code')}
                                    </div>
                                    <div className="sqa-f">
                                        <label htmlFor="sqa-phone2">Secondary Phone Number</label>
                                        <input id="sqa-phone2" type="tel" autoComplete="tel" {...control('field_secondary_phone')} />
                                    </div>
                                </div>
                                <div className="sqa-f" id="sqa-bday-f">
                                    <span className="sqa-lbl" id="sqa-bday-l">What is your Birthday?<Req /></span>
                                    <div className="sqa-bday" role="group" aria-labelledby="sqa-bday-l" {...invalid('field_birthday')}>
                                        <select aria-label="Month" autoComplete="bday-month" {...control('birthdayMonth')}>
                                            <option value="">Month</option>
                                            {MONTHS.map((month, i) => <option key={month} value={DAYS[i]}>{month}</option>)}
                                        </select>
                                        <select aria-label="Day" autoComplete="bday-day" {...control('birthdayDay')}>
                                            {options(DAYS, 'Day')}
                                        </select>
                                        <select aria-label="Year" autoComplete="bday-year" {...control('birthdayYear')}>
                                            {options(YEARS, 'Year')}
                                        </select>
                                    </div>
                                    {message('field_birthday')}
                                </div>
                                <div className="sqa-f" id="sqa-gender-f">
                                    <span className="sqa-lbl" id="sqa-gender-l">Gender<Req /></span>
                                    <div className="sqa-opts" role="radiogroup" aria-labelledby="sqa-gender-l" {...invalid('gender')}>
                                        {radios('gender', GENDERS)}
                                    </div>
                                    {message('gender')}
                                </div>
                                {submitButton('Continue')}
                            </form>
                        </>
                    )}
                    {stage === 3 && (
                        <>
                            {state.showWelcomeTextStep3 && (
                                <p className="sqa-lead">You’re almost done! The more you tell us, the faster we can advance your career.</p>
                            )}
                            <form noValidate onSubmit={submitStep3}>
                                <div className="sqa-f" id="sqa-courses-f">
                                    <span className="sqa-lbl">Select your top 5 areas of interest<Req /></span>
                                    <div className="sqa-cols" {...invalid('appliedCourses')}>
                                        {COURSES.map(([group, courses]) => (
                                            <div className="sqa-cg" key={group}>
                                                <h4>{group}</h4>
                                                {checkboxes('appliedCourses', courses)}
                                            </div>
                                        ))}
                                        <div className="sqa-cg">
                                            <h4>Degrees</h4>
                                            {checkboxes('cuc_degrees', DEGREES)}
                                        </div>
                                    </div>
                                    {message('appliedCourses')}
                                </div>
                                <div className="sqa-f">
                                    <label htmlFor="sqa-story">Tell Us Your Story<Req /></label>
                                    <p className="sqa-note">To help us understand you as an individual and not just an applicant, please tell us about why you are interested in taking this course.</p>
                                    <textarea id="sqa-story" {...control('story')} />
                                    {message('story')}
                                </div>
                                <div className="sqa-row">
                                    <div className="sqa-f">
                                        <label htmlFor="sqa-job">Current Job</label>
                                        <input id="sqa-job" autoComplete="organization-title" {...control('field_current_job')} />
                                    </div>
                                    <div className="sqa-f">
                                        <label htmlFor="sqa-grade">Education Level Completed<Req /></label>
                                        <select id="sqa-grade" {...control('field_highest_grade')}>
                                            {options(GRADES, '- Select -')}
                                        </select>
                                        {message('field_highest_grade')}
                                    </div>
                                </div>
                                <div id="sqa-edu" hidden={!fields.field_highest_grade}>
                                    <div className="sqa-row">
                                        <div className="sqa-f">
                                            <label htmlFor="sqa-uni">Your School or University</label>
                                            <input id="sqa-uni" {...control('field_university')} />
                                        </div>
                                        <div className="sqa-f">
                                            <label htmlFor="sqa-major">Major / Degree / Course Completed</label>
                                            <input id="sqa-major" {...control('field_major')} />
                                        </div>
                                    </div>
                                    <div className="sqa-f">
                                        <label htmlFor="sqa-last">Last Attended</label>
                                        <select id="sqa-last" {...control('field_last_attended')}>
                                            {options(YEARS, '- Select -')}
                                        </select>
                                    </div>
                                </div>
                                <div className="sqa-f">
                                    <span className="sqa-lbl" id="sqa-mil-l">Are you a member of the military?</span>
                                    <div className="sqa-opts" role="radiogroup" aria-labelledby="sqa-mil-l">
                                        {radios('field_military', ['Yes', 'No'])}
                                    </div>
                                </div>
                                <div className="sqa-f" id="sqa-milid" hidden={fields.field_military !== 'Yes'}>
                                    <label htmlFor="sqa-milid-in">Please Provide Your Military ID Number</label>
                                    <p className="sqa-note">(We provide a Military Discount for those who have served)</p>
                                    <input id="sqa-milid-in" {...control('field_military_id')} />
                                </div>
                                <div className="sqa-row">
                                    <div className="sqa-f">
                                        <label htmlFor="sqa-heard">How did you hear about us?</label>
                                        <select id="sqa-heard" {...control('field_sports_recom')}>
                                            {options(HEARD, '- Select -')}
                                        </select>
                                    </div>
                                    <div className="sqa-f">
                                        <label htmlFor="sqa-li">Your LinkedIn URL</label>
                                        <input id="sqa-li" type="url" inputMode="url" autoComplete="url" {...control('field_linkedin_url')} />
                                        {message('field_linkedin_url')}
                                    </div>
                                </div>
                                {submitButton('Submit')}
                                <p className="sqa-note"><strong>NOTE:</strong> You should receive an email confirmation of your application. If you do not, please email us at <a href="mailto:info@smww.com">info@smww.com</a>.</p>
                            </form>
                        </>
                    )}
                    {stage === 4 && (
                        <div className="sqa-done">
                            <p className="sqa-lead"><strong>Thank you for your interest!</strong><br />Someone from SMWW will be contacting you soon. In the meantime, you can view our courses below.</p>
                            <a className="sqa-btn" href={QUICK_APP_DONE_URL}>View our courses</a>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const QuickAppPopup = () => {
    const app = useSyncExternalStore(subscribeQuickApp, getQuickApp);
    // Each application starts with its own empty fields
    return app ? <QuickAppDialog key={app.id} app={app} /> : null;
};

let host = null;

// Opens the popup at the step the application is on. It is a React root of its own: the quick
// forms that open it are in different ones (the page, and the mobile copy inside the page body).
export function openQuickAppPopup() {
    if (!host) {
        host = document.createElement('div');
        document.body.appendChild(host);
        createRoot(host).render(<QuickAppPopup />);
    }
    openQuickApp();
}

export default QuickAppPopup;
